import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/User/Header';
import axios from 'axios';
import { Box, Button, Container, Grid, TextField, Typography, IconButton, Select, MenuItem, InputLabel, FormControl, Paper, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ArrowBack, MyLocation, Map as MapIcon, Layers } from '@mui/icons-material';
import { useSidebar } from "../../components/SidebarContext";
import { getApiUrl, getApiHeaders } from "../../config/api";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/react-datepicker-custom.css";


// Fix default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function ReportFoundItem() {
  const [page, setPage] = useState(1);
  const [itemData, setItemData] = useState({
    item_name: '',
    item_type: '',
    color: '',
    brand_model: '',
    size_dimension: '',
    item_condition: '',
    location_found: '',
    latitude: null,
    longitude: null,
    date_found: '',
    time_found: '',
    unique_identifier: '',
    additional_details: '',
    photo: null,
  });

  const [founderData, setFounderData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    address: '',
    founder_type: '',
    circumstances: '',
  });

  const [analyzingImage, setAnalyzingImage] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapLayer, setMapLayer] = useState('standard'); // 'standard' or 'satellite'
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const layersRef = useRef({});

  const { isSidebarOpen } = useSidebar();
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;

  // Close time picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const timeDialog = document.getElementById('time-picker-compact');
      if (timeDialog && timeDialog.style.display === 'block') {
        if (!timeDialog.contains(event.target) && !event.target.closest('[label="Time Found"]')) {
          timeDialog.style.display = 'none';
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleItemChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'photo') {
      const file = files[0];
      setItemData({ ...itemData, photo: file });
      
      // Auto-analyze image when uploaded
      if (file) {
        analyzeUploadedImage(file);
      }
    } else {
      setItemData({ ...itemData, [name]: value });
    }
  };

  const analyzeUploadedImage = async (file) => {
    setAnalyzingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('photo', file);
      
      const response = await axios.post(getApiUrl('found-item/analyze-image'), formData, {
        headers: getApiHeaders({ "Content-Type": "multipart/form-data" }),
      });
      
      if (response.data.success) {
        const { item_type, color_description, color_details, colors } = response.data.data;
        
        // Auto-fill detected information with multi-color description
        setItemData(prev => ({
          ...prev,
          item_type: item_type || prev.item_type,
          color: color_description || prev.color,  // Use full color description
        }));
        
        // Create detailed message showing all detected colors
        let colorMessage = color_description;
        if (color_details && color_details.length > 0) {
          const colorBreakdown = color_details
            .map(c => `${c.name} (${c.percentage}%)`)
            .join(', ');
          colorMessage = `${color_description}\n\nColor breakdown: ${colorBreakdown}`;
        }
        
        // Show success message with detailed color info
        alert(`✓ Image analyzed!\n\nItem Type: ${item_type || 'Unknown'}\nColors: ${colorMessage}`);
      }
    } catch (error) {
      console.error('Error analyzing image:', error);
      // Don't show error to user, just continue without auto-fill
    } finally {
      setAnalyzingImage(false);
    }
  };

  const handleFounderChange = (e) => {
    const { name, value } = e.target;
    setFounderData({ ...founderData, [name]: value });
  };

  // Initialize map when dialog opens
  useEffect(() => {
    if (mapOpen && !mapRef.current) {
      // Use setTimeout to ensure the DOM element is rendered
      setTimeout(() => {
        const mapContainer = document.getElementById("locationMap");
        if (!mapContainer) {
          console.error("Map container not found");
          return;
        }

        const map = L.map("locationMap", {
          center: [10.713335, 122.562388], // Default center (same as Heatmap)
          zoom: 18,
          minZoom: 3,
          maxZoom: 20, // Safe zoom level that works with all tile layers (~10 meters precision)
          zoomControl: true,
          scrollWheelZoom: true,
        });

        // Standard OpenStreetMap layer
        const standardLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
          maxZoom: 20,
          maxNativeZoom: 19, // OSM tiles only go to 19, but allow zooming to 20
        });

        // Satellite layer (Esri World Imagery)
        const satelliteLayer = L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          {
            attribution: "© Esri, Maxar, Earthstar Geographics",
            maxZoom: 20,
            maxNativeZoom: 19, // Esri tiles go to 19, but allow zooming to 20
          }
        );

        // Hybrid layer (Satellite with labels)
        const hybridLayer = L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
          {
            attribution: "© Esri",
            maxZoom: 20,
            maxNativeZoom: 19, // Labels go to 19, but allow zooming to 20
          }
        );

        // Store layers in ref
        layersRef.current = {
          standard: standardLayer,
          satellite: satelliteLayer,
          hybrid: hybridLayer,
        };

        // Add initial layer based on state
        if (mapLayer === 'satellite') {
          satelliteLayer.addTo(map);
          hybridLayer.addTo(map);
        } else {
          standardLayer.addTo(map);
        }

        mapRef.current = map;

        // Add click handler to place marker
        map.on('click', (e) => {
          const { lat, lng } = e.latlng;
          
          // Remove existing marker if any
          if (markerRef.current) {
            map.removeLayer(markerRef.current);
          }

          // Add new marker
          markerRef.current = L.marker([lat, lng]).addTo(map);
          setMarkerPosition({ lat, lng });
        });

        // If there's already a saved position, show it
        if (itemData.latitude && itemData.longitude) {
          const savedPos = [itemData.latitude, itemData.longitude];
          markerRef.current = L.marker(savedPos).addTo(map);
          setMarkerPosition({ lat: itemData.latitude, lng: itemData.longitude });
          map.setView(savedPos, 18);
        }
      }, 100);
    }

    return () => {
      if (mapRef.current && !mapOpen) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        layersRef.current = {};
      }
    };
  }, [mapOpen, itemData.latitude, itemData.longitude, mapLayer]);

  const handleMapOpen = () => {
    setMapOpen(true);
  };

  const handleMapClose = () => {
    setMapOpen(false);
  };

  const handleMapSave = () => {
    if (markerPosition) {
      setItemData({
        ...itemData,
        latitude: markerPosition.lat,
        longitude: markerPosition.lng,
        location_found: `${markerPosition.lat.toFixed(6)}, ${markerPosition.lng.toFixed(6)}`
      });
    }
    setMapOpen(false);
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          
          if (mapRef.current) {
            // Remove existing marker
            if (markerRef.current) {
              mapRef.current.removeLayer(markerRef.current);
            }

            // Add new marker at current location
            markerRef.current = L.marker([latitude, longitude]).addTo(mapRef.current);
            setMarkerPosition({ lat: latitude, lng: longitude });
            mapRef.current.setView([latitude, longitude], 18);
          }
        },
        (error) => {
          alert("Unable to retrieve your location. Please pin manually on the map.");
          console.error("Geolocation error:", error);
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  // Toggle map layer between standard and satellite
  const handleLayerToggle = () => {
    if (!mapRef.current || !layersRef.current) return;

    const map = mapRef.current;
    const layers = layersRef.current;

    if (mapLayer === 'standard') {
      // Switch to satellite
      map.removeLayer(layers.standard);
      layers.satellite.addTo(map);
      layers.hybrid.addTo(map);
      setMapLayer('satellite');
    } else {
      // Switch to standard
      map.removeLayer(layers.satellite);
      map.removeLayer(layers.hybrid);
      layers.standard.addTo(map);
      setMapLayer('standard');
    }
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();
      Object.entries(itemData).forEach(([key, value]) =>
        formData.append(key, value)
      );
      Object.entries(founderData).forEach(([key, value]) =>
        formData.append(key, value)
      );

      const response = await axios.post(getApiUrl("found-item/report"), formData, {
        headers: getApiHeaders({ "Content-Type": "multipart/form-data" }),
      });

      console.log("Success response:", response);
      alert(response?.data?.message || "Submission successful!");
    } catch (error) {
      console.error("Error submitting:", error);
      alert("Failed to submit: " + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <Box
      sx={{
  transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        bgcolor: "#FBFFE9",
        minHeight: "100vh",
        maxWidth: isSidebarOpen ? (isMobile ? "calc(100vw - 200px)" : "calc(100vw - 300px)") : "100vw",
        overflowX: "hidden",
        width: "100%",
      }}
    >
      <Header title="Report Found Item" />
    <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 3 }, pb: 4 }}>
      <Paper
          elevation={4}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: 4,
            bgcolor: "#E1ECC8",
            borderRadius: "20px",
          }}
        >
      {page === 1 && (
        <Box>
          <Typography variant="h2" component="h2" align="center" sx={{ mb: { xs: 2, md: 4 }, color: "#526D50", fontSize: { xs: "20px", sm: "22px", md: "24px" } }}>
            Step 1: Item Information
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Item Name"
                name="item_name"
                value={itemData.item_name}
                onChange={handleItemChange}
                variant="outlined"
                required
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Item Type</InputLabel>
                <Select
                  name="item_type"
                  value={itemData.item_type}
                  onChange={handleItemChange}
                  label="Item Type"
                  sx={{
                    bgcolor: "#FBFFE9",
                    borderRadius: "15px",
                    borderWidth: "2px",
                    '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                  }}
                >
                  <MenuItem value="Bag">Bag</MenuItem>
                  <MenuItem value="Tumbler">Tumbler</MenuItem>
                  <MenuItem value="Wallet">Wallet</MenuItem>
                  <MenuItem value="Phone">Phone</MenuItem>
                  <MenuItem value="Watch">Watch</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Color"
                name="color"
                value={itemData.color}
                onChange={handleItemChange}
                variant="outlined"
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Brand/Model"
                name="brand_model"
                value={itemData.brand_model}
                onChange={handleItemChange}
                variant="outlined"
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>
          
            {/* Size/Dimension */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={1} alignItems="center">
                {/* Numeric Size Input */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Size/Weight/Volume"
                    name="size"
                    placeholder="e.g. 40x27x12 or 10*15*20"
                    value={itemData.size || ""}
                    onChange={(e) => {
                      const input = e.target.value;

                      // Only allow digits, 'x', and '*'
                      const isValid = /^[0-9x*]*$/.test(input);

                      if (isValid) {
                        setItemData({
                          ...itemData,
                          size: input,
                          size_dimension: `${input} ${itemData.unit || ""}`.trim()
                        });
                      }
                    }}
                    variant="outlined"
                    required
                    sx={{
                      bgcolor: "#FBFFE9",
                      borderRadius: "15px",
                      borderWidth: "2px",
                      '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                    }}
                  />
                </Grid>

                {/* Unit Select Dropdown */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Unit</InputLabel>
                    <Select
                      name="unit"
                      value={itemData.unit || ""}
                      onChange={(e) => {
                        const unit = e.target.value;
                        setItemData({
                          ...itemData,
                          unit,
                          size_dimension: `${itemData.size || ""} ${unit}`.trim()
                        });
                      }}
                      label="Unit"
                      sx={{
                        bgcolor: "#FBFFE9",
                        borderRadius: "15px",
                        borderWidth: "2px",
                        '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 48 * 4,
                          },
                        },
                      }}
                    >
                      <MenuItem value="mm">mm</MenuItem>
                      <MenuItem value="cm">cm</MenuItem>
                      <MenuItem value="m">m</MenuItem>
                      <MenuItem value="in">in</MenuItem>
                      <MenuItem value="ft">ft</MenuItem>

                      <MenuItem value="g">g</MenuItem>
                      <MenuItem value="kg">kg</MenuItem>
                      <MenuItem value="mg">mg</MenuItem>
                      <MenuItem value="lb">lb</MenuItem>
                      <MenuItem value="oz">oz</MenuItem>

                      <MenuItem value="ml">ml</MenuItem>
                      <MenuItem value="l">l</MenuItem>
                      <MenuItem value="fl oz">fl oz</MenuItem>

                      <MenuItem value="pc">pc</MenuItem>
                      <MenuItem value="set">set</MenuItem>
                      <MenuItem value="pack">pack</MenuItem>
                      <MenuItem value="box">box</MenuItem>
                      <MenuItem value="bag">bag</MenuItem>
                      <MenuItem value="bottle">bottle</MenuItem>
                      <MenuItem value="can">can</MenuItem>
                      <MenuItem value="carton">carton</MenuItem>
                      <MenuItem value="roll">roll</MenuItem>
                      <MenuItem value="bundle">bundle</MenuItem>

                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>


            {/* Item Condition Dropdown */}
            <Grid item xs={24} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Item Condition</InputLabel>
                <Select
                  name="item_condition"
                  value={itemData.item_condition}
                  onChange={handleItemChange}
                  label="Item Condition"
                  sx={{
                    bgcolor: "#FBFFE9",
                    borderRadius: "15px",
                    borderWidth: "2px",
                    '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                  }}
                >
                  <MenuItem value="New">New</MenuItem>
                  <MenuItem value="Used">Used</MenuItem>
                  <MenuItem value="Damaged">Damaged</MenuItem>
                  <MenuItem value="Slightly Damaged">Slightly Damaged</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Location Found"
                  name="location_found"
                  value={itemData.location_found}
                  onChange={handleItemChange}
                  variant="outlined"
                  placeholder="Click map icon to pin location"
                  sx={{
                    bgcolor: "#FBFFE9",
                    borderRadius: "15px",
                    borderWidth: "2px",
                    '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleMapOpen}
                  sx={{
                    minWidth: '56px',
                    height: '56px',
                    bgcolor: "#526D50",
                    '&:hover': {
                      bgcolor: "#3e523d",
                    },
                  }}
                >
                  <MapIcon />
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                selected={itemData.date_found ? new Date(itemData.date_found) : null}
                onChange={(date) => {
                  setItemData({
                    ...itemData,
                    date_found: date ? date.toISOString().split('T')[0] : ''
                  });
                }}
                maxDate={new Date()}
                dateFormat="yyyy-MM-dd"
                placeholderText="Date Found"
                customInput={
                  <TextField
                    fullWidth
                    label="Date Found"
                    variant="outlined"
                    sx={{
                      bgcolor: "#FBFFE9",
                      borderRadius: "15px",
                      '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                      '& .MuiInputBase-input': {
                        color: '#526D50',
                        fontWeight: 500,
                      },
                      '& .MuiInputLabel-root': {
                        color: '#526D50',
                      },
                    }}
                  />
                }
                wrapperClassName="date-picker-wrapper"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Time Found"
                value={itemData.time_found ? (() => {
                  const [hour24, minute] = itemData.time_found.split(':');
                  const hour = parseInt(hour24) % 12 || 12;
                  const period = parseInt(hour24) >= 12 ? 'PM' : 'AM';
                  return `${hour}:${minute} ${period}`;
                })() : ''}
                onClick={(e) => {
                  const timeDialog = document.getElementById('time-picker-compact');
                  if (timeDialog) {
                    timeDialog.style.display = timeDialog.style.display === 'block' ? 'none' : 'block';
                  }
                }}
                InputProps={{
                  readOnly: true,
                  sx: {
                    cursor: 'pointer',
                  }
                }}
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                  '& .MuiInputBase-input': {
                    color: '#526D50',
                    fontWeight: 500,
                    cursor: 'pointer',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#526D50',
                  },
                }}
              />
              {/* Compact Time Picker */}
              <Paper
                id="time-picker-compact"
                sx={{
                  display: 'none',
                  position: 'absolute',
                  zIndex: 1300,
                  bgcolor: '#FBFFE9',
                  border: '2px solid #526D50',
                  borderRadius: '15px',
                  mt: 1,
                  width: '280px',
                  boxShadow: '0 4px 12px rgba(82, 109, 80, 0.2)',
                }}
              >
                <Box sx={{ bgcolor: '#526D50', borderRadius: '13px 13px 0 0', p: 1.5 }}>
                  <Typography sx={{ color: '#FBFFE9', fontWeight: 600, textAlign: 'center', fontSize: '1rem' }}>
                    Time
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', p: 1 }}>
                  {/* Hour Column */}
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography sx={{ color: '#FBFFE9', fontWeight: 500, fontSize: '0.875rem', mb: 0.5, bgcolor: '#526D50', borderRadius: '8px 8px 0 0', py: 0.5 }}>
                      Hour
                    </Typography>
                    <Box sx={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #526D50', borderRadius: '0 0 8px 8px' }}>
                      {[...Array(12)].map((_, i) => {
                        const hour = i + 1;
                        const currentHour = itemData.time_found ? parseInt(itemData.time_found.split(':')[0]) % 12 || 12 : null;
                        return (
                          <Box
                            key={hour}
                            onClick={() => {
                              const currentTime = itemData.time_found || '12:00';
                              const [, currentMinute] = currentTime.split(':');
                              const isPM = itemData.time_found && parseInt(itemData.time_found.split(':')[0]) >= 12;
                              const hour24 = isPM ? (hour === 12 ? 12 : hour + 12) : (hour === 12 ? 0 : hour);
                              setItemData({
                                ...itemData,
                                time_found: `${hour24.toString().padStart(2, '0')}:${currentMinute || '00'}`
                              });
                            }}
                            sx={{
                              py: 0.5,
                              cursor: 'pointer',
                              bgcolor: currentHour === hour ? '#526D50' : 'transparent',
                              color: currentHour === hour ? '#FBFFE9' : '#526D50',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              '&:hover': {
                                bgcolor: currentHour === hour ? '#3e5340' : 'rgba(82, 109, 80, 0.1)',
                              },
                            }}
                          >
                            {hour}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                  
                  {/* Minute Column */}
                  <Box sx={{ flex: 1, textAlign: 'center', mx: 1 }}>
                    <Typography sx={{ color: '#FBFFE9', fontWeight: 500, fontSize: '0.875rem', mb: 0.5, bgcolor: '#526D50', borderRadius: '8px 8px 0 0', py: 0.5 }}>
                      Min
                    </Typography>
                    <Box sx={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #526D50', borderRadius: '0 0 8px 8px' }}>
                      {[...Array(60)].map((_, i) => {
                        const currentMinute = itemData.time_found ? parseInt(itemData.time_found.split(':')[1]) : null;
                        return (
                          <Box
                            key={i}
                            onClick={() => {
                              const currentTime = itemData.time_found || '12:00';
                              const [currentHour] = currentTime.split(':');
                              setItemData({
                                ...itemData,
                                time_found: `${currentHour}:${i.toString().padStart(2, '0')}`
                              });
                            }}
                            sx={{
                              py: 0.5,
                              cursor: 'pointer',
                              bgcolor: currentMinute === i ? '#526D50' : 'transparent',
                              color: currentMinute === i ? '#FBFFE9' : '#526D50',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              '&:hover': {
                                bgcolor: currentMinute === i ? '#3e5340' : 'rgba(82, 109, 80, 0.1)',
                              },
                            }}
                          >
                            {i.toString().padStart(2, '0')}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                  
                  {/* AM/PM Column */}
                  <Box sx={{ flex: 0.7, textAlign: 'center' }}>
                    <Typography sx={{ color: '#FBFFE9', fontWeight: 500, fontSize: '0.875rem', mb: 0.5, bgcolor: '#526D50', borderRadius: '8px 8px 0 0', py: 0.5 }}>
                      Period
                    </Typography>
                    <Box sx={{ border: '1px solid #526D50', borderRadius: '0 0 8px 8px' }}>
                      {['AM', 'PM'].map(period => {
                        const isPM = itemData.time_found && parseInt(itemData.time_found.split(':')[0]) >= 12;
                        const currentPeriod = isPM ? 'PM' : 'AM';
                        return (
                          <Box
                            key={period}
                            onClick={() => {
                              const currentTime = itemData.time_found || '12:00';
                              const [currentHour, currentMinute] = currentTime.split(':');
                              const hour12 = parseInt(currentHour) % 12 || 12;
                              const hour24 = period === 'PM' ? (hour12 === 12 ? 12 : hour12 + 12) : (hour12 === 12 ? 0 : hour12);
                              setItemData({
                                ...itemData,
                                time_found: `${hour24.toString().padStart(2, '0')}:${currentMinute || '00'}`
                              });
                            }}
                            sx={{
                              py: 1.5,
                              cursor: 'pointer',
                              bgcolor: currentPeriod === period ? '#526D50' : 'transparent',
                              color: currentPeriod === period ? '#FBFFE9' : '#526D50',
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              '&:hover': {
                                bgcolor: currentPeriod === period ? '#3e5340' : 'rgba(82, 109, 80, 0.1)',
                              },
                            }}
                          >
                            {period}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={24} md={6}>
              <TextField
                fullWidth
                label="Unique Identifier"
                name="unique_identifier"
                value={itemData.unique_identifier}
                onChange={handleItemChange}
                variant="outlined"
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>
            <Grid item xs={24} md={6}>
              <TextField
                fullWidth
                label="Additional Details"
                name="additional_details"
                value={itemData.additional_details}
                onChange={handleItemChange}
                variant="outlined"
                multiline
                rows={4}
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>
            <Grid item xs={24} md={6}>
              <Box>
                <TextField
                  type="file"
                  name="photo"
                  accept="image/*"
                  onChange={handleItemChange}
                  required
                  disabled={analyzingImage}
                  style={{ marginTop: '1rem' }}
                  sx={{
                    bgcolor: "#FBFFE9",
                    borderRadius: "15px",
                    borderWidth: "2px",
                    '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                  }}
                />
                {analyzingImage && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#526D50', fontStyle: 'italic' }}>
                    🔍 Analyzing image to detect item type and color...
                  </Typography>
                )}
                {itemData.photo && !analyzingImage && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#526D50' }}>
                    ✓ Image uploaded and analyzed
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'right' }}>
            <Button variant="contained" onClick={() => setPage(2)}>
              Next
            </Button>
          </Box>
        </Box>
      )}

      {page === 2 && (
        <Box>
          <Typography variant="h2" component="h2" align="center" sx={{ mb: { xs: 2, md: 4 }, color: "#526D50", fontSize: { xs: "20px", sm: "22px", md: "24px" } }}>
            Step 2: Returnee Information
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={founderData.first_name}
                onChange={handleFounderChange}
                variant="outlined"
                required
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={founderData.last_name}
                onChange={handleFounderChange}
                variant="outlined"
                required
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone_number"
                value={founderData.phone_number}
                onChange={handleFounderChange}
                variant="outlined"
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={founderData.email}
                onChange={handleFounderChange}
                variant="outlined"
                type="email"
                required
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={founderData.address}
                onChange={handleFounderChange}
                variant="outlined"
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>

            {/* Founder Type Dropdown */}
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Founder Type</InputLabel>
                <Select
                  name="founder_type"
                  value={founderData.founder_type}
                  onChange={handleFounderChange}
                  label="Founder Type"
                  sx={{
                    bgcolor: "#FBFFE9",
                    borderRadius: "15px",
                    borderWidth: "2px",
                    '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                  }}
                >
                  <MenuItem value="Self">Self</MenuItem>
                  <MenuItem value="Another">Another</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Circumstances of Finding"
                name="circumstances"
                value={founderData.circumstances}
                onChange={handleFounderChange}
                variant="outlined"
                multiline
                rows={4}
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  borderWidth: "2px",
                  '& fieldset': { borderRadius: "15px", borderWidth: "2px" },
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <IconButton onClick={() => setPage(1)}>
              <ArrowBack />
            </IconButton>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )}
      </Paper>
    </Container>

    {/* Map Dialog */}
    <Dialog 
      open={mapOpen} 
      onClose={handleMapClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: "#E1ECC8",
        }
      }}
    >
      <DialogTitle sx={{ color: "#526D50", fontWeight: 600 }}>
        Pin Location on Map
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: "#516d4f" }}>
          Click on the map to pin the exact location where you found the item, or use your current location.
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<MyLocation />}
            onClick={handleUseCurrentLocation}
            sx={{
              borderColor: "#526D50",
              color: "#526D50",
              '&:hover': {
                borderColor: "#3e523d",
                bgcolor: "rgba(82, 109, 80, 0.08)",
              },
            }}
          >
            Use Current Location
          </Button>
          <Button
            variant="outlined"
            startIcon={<Layers />}
            onClick={handleLayerToggle}
            sx={{
              borderColor: "#526D50",
              color: "#526D50",
              '&:hover': {
                borderColor: "#3e523d",
                bgcolor: "rgba(82, 109, 80, 0.08)",
              },
            }}
          >
            {mapLayer === 'standard' ? 'Satellite View' : 'Standard View'}
          </Button>
        </Box>
        <Box
          id="locationMap"
          sx={{
            height: "400px",
            width: "100%",
            borderRadius: "15px",
            overflow: "hidden",
            border: "2px solid #d0e7ba",
          }}
        />
        {markerPosition && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: "#516d4f" }}>
            Selected: {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleMapClose}
          sx={{
            color: "#516d4f",
            '&:hover': {
              bgcolor: "rgba(81, 109, 79, 0.08)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleMapSave}
          variant="contained"
          disabled={!markerPosition}
          sx={{
            bgcolor: "#526D50",
            '&:hover': {
              bgcolor: "#3e523d",
            },
            '&:disabled': {
              bgcolor: "#d1dfb5",
            },
          }}
        >
          Save Location
        </Button>
      </DialogActions>
    </Dialog>
  </Box>
  );
}

export default ReportFoundItem;
